// @bienlivrer/config — Configuration partagée entre apps
// Ce package centralise les constantes métier communes au frontend et à l'API.

// ── Géographie cible ─────────────────────────────────────────
export const TARGET_REGION = {
  name: 'Biel/Bienne',
  country: 'CH',
  zipCodes: ['2500', '2501', '2502', '2503', '2504', '2505', '2560', '2575'],
  center: { lat: 47.1368, lng: 7.2467 },
  maxDeliveryRadiusKm: 15,
} as const;

// ── Commissions & Frais ───────────────────────────────────────
export const COMMISSION = {
  starter: 8,   // %
  pro: 6,        // %
  premium: 4,    // %
} as const;

/** @deprecated Utiliser DEFAULT_DELIVERY_PRICING et calculateDeliveryFeeFromPricing */
export const DELIVERY_FEE = {
  baseChf: 2.50,
  perKmChf: 0.80,
  maxChf: 8.00,
  freeAboveChf: 50,
} as const;

// ── Grille livraison client (admin) ───────────────────────────
export interface DeliveryPricingTier {
  maxKm: number;
  feeChf: number;
}

export interface DeliveryPricingConfig {
  maxPlatformRadiusKm: number;
  peakHourSurchargeChf: number;
  urgentSurchargeChf: number;
  tiers: DeliveryPricingTier[];
  /** false = illimité (pilote tournée mutualisée) */
  driverCapacityLimitEnabled: boolean;
  /** Valeur appliquée lorsque driverCapacityLimitEnabled = true */
  driverMaxConcurrentDeliveries: number;
}

export interface DriverDeliveryCapacity {
  active: number;
  max: number | null;
  available: number | null;
  limitEnabled: boolean;
}

/** Grille 1 km jusqu'à 10 km — calibrée vs barème livreur (3,50 + 0,50/km après 1 km). */
export const DEFAULT_DELIVERY_PRICING: DeliveryPricingConfig = {
  maxPlatformRadiusKm: 10,
  peakHourSurchargeChf: 1,
  urgentSurchargeChf: 2,
  driverCapacityLimitEnabled: false,
  driverMaxConcurrentDeliveries: 15,
  tiers: [
    { maxKm: 1, feeChf: 2.9 },
    { maxKm: 2, feeChf: 3.9 },
    { maxKm: 3, feeChf: 4.9 },
    { maxKm: 4, feeChf: 5.5 },
    { maxKm: 5, feeChf: 5.9 },
    { maxKm: 6, feeChf: 6.9 },
    { maxKm: 7, feeChf: 7.9 },
    { maxKm: 8, feeChf: 8.9 },
    { maxKm: 9, feeChf: 9.9 },
    { maxKm: 10, feeChf: 10.9 },
  ],
};

export function parseDeliveryPricingTiers(raw: unknown): DeliveryPricingTier[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_DELIVERY_PRICING.tiers;
  const tiers = raw
    .map((t) => ({
      maxKm: Number((t as DeliveryPricingTier).maxKm),
      feeChf: Number((t as DeliveryPricingTier).feeChf),
    }))
    .filter((t) => Number.isFinite(t.maxKm) && t.maxKm > 0 && Number.isFinite(t.feeChf) && t.feeChf >= 0)
    .sort((a, b) => a.maxKm - b.maxKm);
  return tiers.length > 0 ? tiers : DEFAULT_DELIVERY_PRICING.tiers;
}

export function calculateDeliveryFeeFromPricing(
  distanceKm: number,
  pricing: DeliveryPricingConfig,
  options?: { urgent?: boolean; at?: Date },
): number {
  const { urgent = false, at = new Date() } = options ?? {};
  const sorted = [...pricing.tiers].sort((a, b) => a.maxKm - b.maxKm);
  const lastTier = sorted[sorted.length - 1];
  const platformMax = Math.min(
    pricing.maxPlatformRadiusKm,
    lastTier?.maxKm ?? pricing.maxPlatformRadiusKm,
  );

  if (distanceKm > platformMax) return -1;

  let fee = -1;
  for (const tier of sorted) {
    if (distanceKm <= tier.maxKm) {
      fee = tier.feeChf;
      break;
    }
  }
  if (fee < 0) return -1;

  if (urgent && fee > 0) fee += pricing.urgentSurchargeChf;

  const hour = at.getHours();
  const isWeekday = at.getDay() >= 1 && at.getDay() <= 5;
  const isPeakHour = (hour >= 12 && hour < 14) || (hour >= 17 && hour < 20);
  if (isWeekday && isPeakHour && fee > 0) fee += pricing.peakHourSurchargeChf;

  return Math.round(fee * 100) / 100;
}

export function getDeliveryFeeRangeForRadius(
  radiusKm: number,
  pricing: DeliveryPricingConfig,
  options?: { at?: Date },
): { min: number; max: number } | null {
  const effectiveRadius = Math.min(radiusKm, pricing.maxPlatformRadiusKm);
  if (effectiveRadius <= 0) return null;
  const min = calculateDeliveryFeeFromPricing(0.001, pricing, options);
  const max = calculateDeliveryFeeFromPricing(effectiveRadius, pricing, options);
  if (min < 0 || max < 0) return null;
  return { min, max };
}

export type DeliveryZoneRejectReason = 'out_of_shop_radius' | 'out_of_platform_zone';

export function validateDeliveryDistance(
  distanceKm: number,
  shopRadiusKm: number,
  pricing: DeliveryPricingConfig,
): { ok: true } | { ok: false; reason: DeliveryZoneRejectReason } {
  if (distanceKm > shopRadiusKm) return { ok: false, reason: 'out_of_shop_radius' };
  const fee = calculateDeliveryFeeFromPricing(distanceKm, pricing);
  if (fee < 0) return { ok: false, reason: 'out_of_platform_zone' };
  return { ok: true };
}

export interface DeliveryChargeSplit {
  /** Montant facturé au client (0 si offerte). */
  clientDeliveryFee: number;
  /** Montant déduit du reversement commerçant (0 si le client ou la plateforme paie). */
  merchantCoveredDeliveryFee: number;
}

/**
 * Qui paie la course une fois le tarif grille connu (≥ 0).
 * - Seuil boutique atteint : le commerçant paie (reversement), client à 0.
 * - Coupon plateforme « livraison offerte » : la plateforme paie, commerçant à 0.
 * - Sinon : le client paie.
 */
export function splitDeliveryCharge(opts: {
  gridFeeChf: number;
  subtotal: number;
  freeDeliveryAbove?: number | null;
  platformWaivesDelivery?: boolean;
}): DeliveryChargeSplit {
  const grid = roundChf(Number(opts.gridFeeChf));
  if (!Number.isFinite(grid) || grid <= 0) {
    return { clientDeliveryFee: 0, merchantCoveredDeliveryFee: 0 };
  }
  if (opts.platformWaivesDelivery) {
    return { clientDeliveryFee: 0, merchantCoveredDeliveryFee: 0 };
  }
  const threshold = Number(opts.freeDeliveryAbove);
  if (Number.isFinite(threshold) && threshold > 0 && Number(opts.subtotal) >= threshold) {
    return { clientDeliveryFee: 0, merchantCoveredDeliveryFee: grid };
  }
  return { clientDeliveryFee: grid, merchantCoveredDeliveryFee: 0 };
}

/** Net commerçant d’une commande : sous-total − commission − livraison offerte par le magasin. */
export function merchantOrderPayoutLine(opts: {
  subtotal: number;
  commissionRatePercent: number;
  merchantCoveredDeliveryFee?: number | null;
}): {
  gross: number;
  commission: number;
  merchantCoveredDeliveryFee: number;
  net: number;
} {
  const gross = roundChf(Number(opts.subtotal) || 0);
  const rate = Number(opts.commissionRatePercent) || 0;
  const commission = roundChf(gross * (rate / 100));
  const covered = roundChf(Math.max(0, Number(opts.merchantCoveredDeliveryFee) || 0));
  return {
    gross,
    commission,
    merchantCoveredDeliveryFee: covered,
    net: roundChf(gross - commission - covered),
  };
}

/** Barème livreur documenté — aligné deliveries.service.ts */
export function estimateDriverPayoutChf(distanceKm: number): number {
  const base = 3.5;
  const distanceBonus = distanceKm > 1 ? (distanceKm - 1) * 0.5 : 0;
  return Math.round((base + distanceBonus) * 100) / 100;
}

/** Frais de service client (inclut traitement paiement PSP + marge plateforme légère). */
export const PLATFORM_SERVICE_FEE = {
  ratePercent: 4,
  fixedChf: 0.5,
  minChf: 1.0,
  maxChf: 4.0,
} as const;

/** Applique la majoration commerçant au prix catalogue (arrondi au centime). */
export function applyPriceMarkup(basePrice: number, markupPercent: number): number {
  if (!Number.isFinite(basePrice) || basePrice < 0) return 0;
  const pct = Number.isFinite(markupPercent) ? Math.max(0, markupPercent) : 0;
  const raw = basePrice * (1 + pct / 100);
  return Math.round(raw * 100) / 100;
}

export type ShopMarkupCommission = {
  commissionRate?: number | null;
  trialEndsAt?: Date | string | null;
};

/** Essai 90 j en cours : `trialEndsAt` exclusif (aligné reversements). */
export function isMerchantTrialActive(
  trialEndsAt: Date | string | null | undefined,
  at: Date = new Date(),
): boolean {
  if (!trialEndsAt) return false;
  const end = trialEndsAt instanceof Date ? trialEndsAt : new Date(trialEndsAt);
  if (Number.isNaN(end.getTime())) return false;
  return at < end;
}

/** Plafond de majoration = commission effective (0 % pendant l’essai, sinon 8 / 6 / 4). */
export function maxPriceMarkupPercent(shop: ShopMarkupCommission, at: Date = new Date()): number {
  if (isMerchantTrialActive(shop.trialEndsAt, at)) return 0;
  const rate = Number(shop.commissionRate);
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return rate;
}

/** Majoration réellement appliquée au client (jamais au-dessus de la commission, 0 pendant l’essai). */
export function effectivePriceMarkupPercent(
  storedMarkupPercent: number,
  shop: ShopMarkupCommission,
  at: Date = new Date(),
): number {
  const max = maxPriceMarkupPercent(shop, at);
  const n = Number(storedMarkupPercent);
  if (!Number.isFinite(n) || n <= 0 || max <= 0) return 0;
  return Math.min(n, max);
}

/** Calcule le frais de service sur le sous-total produits (hors livraison). */
export function calculatePlatformServiceFee(subtotal: number): number {
  const { ratePercent, fixedChf, minChf, maxChf } = PLATFORM_SERVICE_FEE;
  const raw = subtotal * (ratePercent / 100) + fixedChf;
  const clamped = Math.min(maxChf, Math.max(minChf, raw));
  return Math.round(clamped * 100) / 100;
}

// ── Paiements ────────────────────────────────────────────────
/** Paiement en ligne (carte + TWINT via Stripe Payment Element). */
export const PAYMENT_METHODS = ['stripe'] as const;
/** Espèces au retrait (click & collect) — option complémentaire selon config boutique / plateforme. */
export const CASH_PICKUP_PAYMENT_METHOD = 'cash' as const;
export const CURRENCY = 'CHF';

/** Défauts espèces au retrait (plateforme). */
export const CASH_PAYMENT_DEFAULTS = {
  /** Commandes en ligne terminées requises avant espèces (2 → cash dès la 3e commande). */
  minCompletedOnlineOrders: 2,
} as const;

// ── Limites MVP ──────────────────────────────────────────────
export const MVP_LIMITS = {
  maxShopsAtLaunch: 50,
  maxOrdersPerDay: 200,
  maxProductsPerShop: 500,
} as const;

// ── Abonnements commerçants ───────────────────────────────────
export const SUBSCRIPTIONS = {
  starter: {
    label: { fr: 'Démarrage', de: 'Starter' },
    priceChfPerMonth: 0,
    commissionRate: 8,
    maxProducts: 50,
  },
  pro: {
    label: { fr: 'Pro', de: 'Pro' },
    priceChfPerMonth: 49,
    commissionRate: 6,
    maxProducts: 200,
  },
  premium: {
    label: { fr: 'Premium', de: 'Premium' },
    priceChfPerMonth: 99,
    commissionRate: 4,
    maxProducts: 500,
  },
} as const;

export type SubscriptionTier = keyof typeof SUBSCRIPTIONS;

/** Début d’un abonnement commerçant : 1er du mois suivant, 00:00 Europe/Zurich. */
const ZURICH_TZ = 'Europe/Zurich';

function getZurichOffsetMs(date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ZURICH_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const bag: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') bag[p.type] = p.value;
  }
  const asUtc = Date.UTC(
    Number(bag.year),
    Number(bag.month) - 1,
    Number(bag.day),
    Number(bag.hour),
    Number(bag.minute),
    Number(bag.second),
  );
  return asUtc - date.getTime();
}

function zurichMidnight(year: number, month: number, day: number): Date {
  const utcGuess = Date.UTC(year, month - 1, day, 0, 0, 0);
  const date = new Date(utcGuess);
  const offset1 = getZurichOffsetMs(date);
  let local = new Date(utcGuess - offset1);
  const offset2 = getZurichOffsetMs(local);
  if (offset2 !== offset1) local = new Date(utcGuess - offset2);
  return local;
}

function zurichYmd(date: Date): { year: number; month: number; day: number; hour: number; minute: number } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ZURICH_TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const bag: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') bag[p.type] = p.value;
  }
  return {
    year: Number(bag.year),
    month: Number(bag.month),
    day: Number(bag.day),
    hour: Number(bag.hour),
    minute: Number(bag.minute),
  };
}

/**
 * Minuit du 1er du mois civil qui suit `now`, fuseau Europe/Zurich.
 * Une activation le 25 septembre démarre le 1er octobre à 00:00.
 * Une activation le 1er octobre à 00:30 démarre le 1er novembre à 00:00.
 */
export function nextMerchantSubscriptionStart(now: Date = new Date()): Date {
  const { year, month } = zurichYmd(now);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return zurichMidnight(nextYear, nextMonth, 1);
}

/** Vrai si l’instant est un 1er à 00:00 en heure suisse. */
export function isZurichFirstMidnight(date: Date): boolean {
  const parts = zurichYmd(date);
  return parts.day === 1 && parts.hour === 0 && parts.minute === 0;
}

/** Plafond catalogue selon l'offre commerçant (Starter par défaut si inconnu). */
export function getMaxProductsForSubscription(subscription: string): number {
  if (subscription in SUBSCRIPTIONS) {
    return SUBSCRIPTIONS[subscription as SubscriptionTier].maxProducts;
  }
  return SUBSCRIPTIONS.starter.maxProducts;
}

/** Vérifie si l'ajout de produits reste sous le plafond de l'offre. */
export function canAddCatalogProducts(
  currentCount: number,
  subscription: string,
  additional = 1,
): boolean {
  return currentCount + additional <= getMaxProductsForSubscription(subscription);
}

// ── i18n ─────────────────────────────────────────────────────
/** Langues UI + contenu métier (nom produit, commerce, traduction auto). */
export const CONTENT_LANGUAGES = ['fr', 'de', 'en', 'it'] as const;
export type ContentLanguage = (typeof CONTENT_LANGUAGES)[number];

export const UI_LANGUAGES = CONTENT_LANGUAGES;
export type UiLanguage = ContentLanguage;

export const SUPPORTED_LANGUAGES = CONTENT_LANGUAGES;
export type SupportedLanguage = ContentLanguage;
export const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';

// ── Statuts de commande (ordre de flux) ──────────────────────
export const ORDER_STATUS_FLOW = [
  'pending_payment',
  'pending',
  'accepted',
  'ready',
  'pickup',
  'delivering',
  'delivered',
  'completed',
] as const;

// ── TVA Suisse ───────────────────────────────────────────────
export const VAT_RATES = {
  standard: 8.1,
  reduced: 2.6,
  special: 3.8,
} as const;

// ── Conditions générales (CGV/CGU) ───────────────────────────
/** Version courante publiée sur /cgv et /terms — incrémenter à chaque mise à jour juridique. */
export const LEGAL_TERMS = {
  version: '20260621',
  documents: ['cgv', 'terms'] as const,
} as const;

/** Caution d’emballage (consigne) — montant par unité, hors TVA, hors commission. */
export const PACKAGING_DEPOSIT = {
  minChf: 0.5,
  maxChf: 80,
} as const;

export const PACKAGING_DEPOSIT_STATUS = {
  none: 'none',
  pending: 'pending',
  held: 'held',
  refunded: 'refunded',
} as const;

export type PackagingDepositStatus =
  (typeof PACKAGING_DEPOSIT_STATUS)[keyof typeof PACKAGING_DEPOSIT_STATUS];

export function roundChf(n: number): number {
  return Math.round(Number(n) * 100) / 100;
}

/** Montant unitaire facturable, ou 0 si le bouton n’est pas actif / montant hors plage. */
export function resolvedPackagingDepositUnit(
  enabled: boolean | null | undefined,
  amount: number | null | undefined,
): number {
  if (!enabled) return 0;
  const a = roundChf(Number(amount ?? 0));
  if (!Number.isFinite(a) || a < PACKAGING_DEPOSIT.minChf) return 0;
  return Math.min(a, PACKAGING_DEPOSIT.maxChf);
}

export function linePackagingDepositTotal(unitAmount: number, quantity: number): number {
  return roundChf(Math.max(0, Number(unitAmount) || 0) * Math.max(0, Number(quantity) || 0));
}

/**
 * Part du remboursement Stripe qui porte sur les produits (pas la caution rendue).
 * Un remboursement de caution seul ne doit pas sortir la commande du reversement commerçant.
 */
export function payoutProductRefundAmount(params: {
  paymentRefundAmount?: number | null;
  packagingDepositTotal?: number | null;
  packagingDepositStatus?: string | null;
}): number {
  const refunded = roundChf(Number(params.paymentRefundAmount ?? 0));
  const depositBack =
    params.packagingDepositStatus === PACKAGING_DEPOSIT_STATUS.refunded
      ? roundChf(Number(params.packagingDepositTotal ?? 0))
      : 0;
  return roundChf(Math.max(0, refunded - depositBack));
}

export function orderNeedsPayoutRefundReview(params: {
  paymentRefundAmount?: number | null;
  packagingDepositTotal?: number | null;
  packagingDepositStatus?: string | null;
}): boolean {
  return payoutProductRefundAmount(params) > 0.01;
}

/**
 * Tarifs Stripe publics Suisse (cartes / TWINT) — pas de frais d’émission de remboursement
 * hors virement bancaire. Le % de l’encaissement d’origine n’est pas rendu.
 * https://stripe.com/en-ch/pricing et /pricing/local-payment-methods
 */
export const STRIPE_CH_PSP = {
  twintPercent: 1.9,
  domesticCardPercent: 2.9,
  internationalCardPercent: 3.25,
  fixedChfPerCharge: 0.3,
} as const;

/** Part du frais Stripe qui porte sur le volume de caution (le fixe 0.30 CHF n’est pas incrémental). */
export function unrecoveredPspOnDeposit(depositChf: number, ratePercent: number): number {
  const d = roundChf(Math.max(0, Number(depositChf) || 0));
  const p = Number(ratePercent);
  if (!Number.isFinite(p) || p <= 0) return 0;
  return roundChf((d * p) / 100);
}

export {
  MERCHANT_LEGAL_FORMS,
  MERCHANT_SIGNATORY_FUNCTIONS,
  isMerchantLegalFormCode,
  isMerchantSignatoryFunctionCode,
  merchantLegalFormLabel,
  merchantSignatoryFunctionLabel,
} from './merchantContractIdentity.ts';
export type { MerchantLegalFormCode, MerchantSignatoryFunctionCode } from './merchantContractIdentity.ts';

